import { Alert, Platform } from 'react-native';

// Declare window type for web
declare const window: any;

// Global state to manage web modals and toasts
let webModalManager: WebModalManager | null = null;
let webToastManager: WebToastManager | null = null;

export interface WebModalManager {
  showConfirmModal: (config: {
    title: string;
    message: string;
    confirmText: string;
    cancelText: string;
    onConfirm: () => void;
    onCancel?: () => void;
  }) => void;
}

export interface WebToastManager {
  showInfo: (message: string) => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showWarning: (message: string) => void;
}

/**
 * Register the web modal manager (call this from your ToastContext or App root)
 */
export const registerWebModalManager = (manager: WebModalManager) => {
  webModalManager = manager;
};

/**
 * Register the web toast manager (call this from your ToastContext or App root)
 */
export const registerWebToastManager = (manager: WebToastManager) => {
  webToastManager = manager;
};

/**
 * Cross-platform alert that works on web and native
 * On native: Uses Alert.alert
 * On web: Uses toast notifications
 */
export const showAlert = (
  title: string,
  message?: string,
  buttons?: Array<{ text: string; onPress?: () => void; style?: 'default' | 'cancel' | 'destructive' }>
) => {
  if (Platform.OS === 'web') {
    // On web, use toast notifications
    const onPress = buttons?.[0]?.onPress;
    const fullMessage = message ? `${title}: ${message}` : title;
    
    if (webToastManager) {
      // Determine toast type based on button style if available
      const buttonStyle = buttons?.[0]?.style;
      if (buttonStyle === 'destructive') {
        webToastManager.showError(fullMessage);
      } else {
        webToastManager.showInfo(fullMessage);
      }
    } else {
      // Fallback to browser alert if toast manager not registered
      if (typeof window !== 'undefined') {
        window.alert(`${title}\n\n${message || ''}`);
      }
    }
    
    if (onPress) {
      onPress();
    }
  } else {
    // On native, use the standard Alert.alert
    Alert.alert(
      title,
      message,
      buttons?.map(btn => ({
        text: btn.text,
        onPress: btn.onPress,
        style: btn.style,
      }))
    );
  }
};

/**
 * Cross-platform confirmation dialog
 * On native: Uses Alert.alert with buttons
 * On web: Uses modal manager or window.confirm fallback
 * Returns a promise that resolves to true if confirmed, false if cancelled
 */
export const showConfirm = async (
  title: string,
  message: string,
  confirmText: string = 'OK',
  cancelText: string = 'Cancel'
): Promise<boolean> => {
  if (Platform.OS === 'web') {
    // Try to use the web modal manager first
    if (webModalManager) {
      return new Promise<boolean>((resolve) => {
        webModalManager!.showConfirmModal({
          title,
          message,
          confirmText,
          cancelText,
          onConfirm: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
    }
    
    // Fallback to browser confirm dialog
    if (typeof window !== 'undefined') {
      return window.confirm(`${title}\n\n${message}`);
    }
    return false;
  } else {
    // On native, use Alert.alert with promise
    return new Promise<boolean>((resolve) => {
      Alert.alert(
        title,
        message,
        [
          {
            text: cancelText,
            style: 'cancel',
            onPress: () => resolve(false),
          },
          {
            text: confirmText,
            style: 'default',
            onPress: () => resolve(true),
          },
        ],
        { cancelable: true, onDismiss: () => resolve(false) }
      );
    });
  }
};

/**
 * Cross-platform confirmation with custom button handlers
 * On native: Uses Alert.alert
 * On web: Uses modal manager or window.confirm
 */
export const showConfirmWithButtons = (
  title: string,
  message: string,
  confirmText: string,
  cancelText: string,
  onConfirm: () => void,
  onCancel?: () => void
) => {
  if (Platform.OS === 'web') {
    // Try to use the web modal manager first
    if (webModalManager) {
      webModalManager.showConfirmModal({
        title,
        message,
        confirmText,
        cancelText,
        onConfirm,
        onCancel: onCancel || (() => {}),
      });
    } else {
      // Fallback to browser confirm dialog
      if (typeof window !== 'undefined') {
        const confirmed = window.confirm(`${title}\n\n${message}`);
        if (confirmed) {
          onConfirm();
        } else if (onCancel) {
          onCancel();
        }
      }
    }
  } else {
    // On native, use Alert.alert
    Alert.alert(
      title,
      message,
      [
        {
          text: cancelText,
          style: 'cancel',
          onPress: onCancel,
        },
        {
          text: confirmText,
          style: 'default',
          onPress: onConfirm,
        },
      ],
      { cancelable: true }
    );
  }
};

/**
 * Cross-platform photo picker action sheet
 * On iOS: Uses ActionSheetIOS (works correctly from modal screens)
 * On Android/Web: Uses showConfirmWithButtons
 * 
 * This function handles the known issue where Alert.alert doesn't show
 * when called from screens presented with presentation: 'modal' in React Navigation.
 */
export const showPhotoPickerActionSheet = (
  onSelectLibrary: () => void,
  onSelectCamera: () => void,
  title: string = 'Add Photos'
) => {
  if (Platform.OS === 'ios') {
    // Dynamically require ActionSheetIOS to avoid web bundle issues
    const { ActionSheetIOS } = require('react-native');
    ActionSheetIOS.showActionSheetWithOptions(
      {
        options: ['Photo Library', 'Take Photo', 'Cancel'],
        cancelButtonIndex: 2,
        title,
      },
      (buttonIndex: number) => {
        if (buttonIndex === 0) onSelectLibrary();
        else if (buttonIndex === 1) onSelectCamera();
      }
    );
  } else {
    // Android and web
    showConfirmWithButtons(
      title,
      'Choose how you want to add photos',
      'Photo Library',
      'Take Photo',
      onSelectLibrary,
      onSelectCamera
    );
  }
};
